import { Link } from "react-router-dom";

import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAirConditioning,
  IconBeach,
  IconChevronRight,
  IconHomeHeart,
  IconLeaf,
  IconMountain,
  IconSparkles,
  IconSun,
  IconSwimming,
  IconWifi,
} from "@tabler/icons-react";

import { t } from "i18next";

import { trackEvent } from "@/tracking";

const featureIcons = [
  IconSwimming,
  IconHomeHeart,
  IconAirConditioning,
  IconMountain,
  IconLeaf,
  IconSparkles,
  IconWifi,
  IconBeach,
  IconSun,
  IconHomeHeart,
  IconWifi,
  IconBeach,
  IconLeaf,
];

export function Home() {
  const features = t("home.features", { returnObjects: true }) as string[];

  return (
    <Stack gap={0}>
      <Box
        component="section"
        style={{
          position: "relative",
          minHeight: "min(82dvh, 760px)",
          width: "100%",
          overflow: "hidden",
          isolation: "isolate",
        }}
      >
        <Box
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "url('/img/pool-day.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: "scale(1.05)",
            zIndex: -2,
          }}
        />
        <Box
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(254,102,196,0.18) 0%, rgba(55,182,255,0.25) 45%, light-dark(rgba(255,255,255,0.55), rgba(11,23,41,0.55)) 100%)",
            zIndex: -1,
          }}
        />

        <Container size="xl" h="100%" py={{ base: "xl", sm: 80 }}>
          <Stack
            justify="center"
            align="flex-start"
            gap="lg"
            mih="calc(min(82dvh, 760px) - 8rem)"
            maw={720}
          >
            <Badge
              size="lg"
              radius="xl"
              variant="white"
              color="sea"
              leftSection={<IconSun size={14} />}
              style={{ backdropFilter: "blur(6px)" }}
            >
              Rodakino · South Crete
            </Badge>
            <Title
              order={1}
              c="white"
              style={{
                textShadow: "0 4px 30px rgba(0,0,0,0.35)",
                fontWeight: 600,
              }}
            >
              {t("home.header")}
            </Title>
            <Text
              size="lg"
              c="white"
              style={{
                textShadow: "0 2px 12px rgba(0,0,0,0.3)",
                maxWidth: 620,
              }}
            >
              {t("home.paragraph1")}
            </Text>
            <Group gap="sm" mt="xs">
              <Button
                component={Link}
                to="/book"
                size="lg"
                color="blossom"
                radius="xl"
                rightSection={<IconChevronRight size={18} />}
                onClick={() =>
                  trackEvent("Book CTA Click", { location: "home-hero" })
                }
              >
                {t("menu.book")}
              </Button>
              <Button
                component={Link}
                to="/gallery"
                size="lg"
                variant="white"
                color="sea"
                radius="xl"
                onClick={() =>
                  trackEvent("Gallery CTA Click", { location: "home-hero" })
                }
              >
                {t("menu.gallery")}
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={{ base: "xl", sm: 80 }}>
        <Grid gutter={{ base: "lg", sm: "xl" }} align="stretch">
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="md" h="100%" justify="center">
              <Badge
                variant="light"
                color="blossom"
                radius="sm"
                w="fit-content"
              >
                Highlights
              </Badge>
              <Title order={2}>{t("home.featuresList")}</Title>
              <Text c="dimmed">
                Every detail tuned for slow mornings, sun-soaked afternoons and
                starlit suppers by the pool.
              </Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card withBorder padding="lg">
              <List spacing="sm" size="md" listStyleType="none">
                {features.map((item, index) => {
                  const Icon = featureIcons[index % featureIcons.length];
                  return (
                    <List.Item
                      key={index}
                      icon={
                        <ThemeIcon
                          size={28}
                          radius="xl"
                          variant="light"
                          color={index % 2 === 0 ? "sea" : "blossom"}
                        >
                          <Icon size={16} />
                        </ThemeIcon>
                      }
                    >
                      {item}
                    </List.Item>
                  );
                })}
              </List>
            </Card>
          </Grid.Col>
        </Grid>
      </Container>

      <Box
        py={{ base: "xl", sm: 80 }}
        style={{
          background:
            "light-dark(linear-gradient(180deg, var(--mantine-color-sea-0) 0%, transparent 100%), linear-gradient(180deg, rgba(55,182,255,0.08) 0%, transparent 100%))",
        }}
      >
        <Container size="lg">
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
            <Card withBorder p="lg">
              <ThemeIcon size={44} radius="xl" variant="light" color="sun">
                <IconSwimming size={22} />
              </ThemeIcon>
              <Title order={4} mt="sm">
                Private pool
              </Title>
              <Text c="dimmed" size="sm" mt={4}>
                Your own oasis surrounded by aromatic gardens.
              </Text>
            </Card>
            <Card withBorder p="lg">
              <ThemeIcon size={44} radius="xl" variant="light" color="sea">
                <IconMountain size={22} />
              </ThemeIcon>
              <Title order={4} mt="sm">
                Sea & mountain views
              </Title>
              <Text c="dimmed" size="sm" mt={4}>
                Wake to the Libyan Sea, dine watching the sun melt into it.
              </Text>
            </Card>
            <Card withBorder p="lg">
              <ThemeIcon size={44} radius="xl" variant="light" color="blossom">
                <IconHomeHeart size={22} />
              </ThemeIcon>
              <Title order={4} mt="sm">
                Cretan craft
              </Title>
              <Text c="dimmed" size="sm" mt={4}>
                Handmade furniture and artisanal touches throughout.
              </Text>
            </Card>
          </SimpleGrid>
        </Container>
      </Box>

      <Container size="md" py={{ base: "xl", sm: 80 }}>
        <Stack gap="md" align="center" ta="center">
          <Badge variant="light" color="sea" radius="sm">
            Your home in Crete
          </Badge>
          <Title order={2}>An oasis of calm</Title>
          <Text size="lg" c="dimmed">
            {t("home.outro")}
          </Text>
          <Button
            component={Link}
            to="/book"
            mt="md"
            size="lg"
            radius="xl"
            color="blossom"
            rightSection={<IconChevronRight size={18} />}
            onClick={() =>
              trackEvent("Book CTA Click", { location: "home-outro" })
            }
          >
            {t("menu.book")}
          </Button>
        </Stack>
      </Container>
    </Stack>
  );
}

export default Home;
